import React from 'react';
import { useTranslation } from 'react-i18next';
import { TemplateType } from '../TemplateType';
import usePdfContent, { PdfContentParams } from '../usePdfContent';
import { useLocation } from 'react-router-dom';
import { makePath } from 'apps/Pdf/helpers/helpers';

import Grid from '@material-ui/core/Grid';

import { translatePortfolioCode, translatePortfolioType } from 'helpers/helpers';
import PdfContent from 'apps/Pdf/components/common/PdfContent';
import Title from 'apps/Pdf/components/common/Title';
import H1 from 'apps/Pdf/components/common/H1';
import H2 from 'apps/Pdf/components/common/H2';
import H3 from 'apps/Pdf/components/common/H3';
import P from 'apps/Pdf/components/common/P';
import Spacer from 'apps/Pdf/components/common/Spacer';
import PageBreak from 'apps/Pdf/components/common/PageBreak';
import BasicData from 'apps/Pdf/components/BasicData/BasicData';
import Glossary from 'apps/Pdf/components/Glossary/Glossary';
import Disclaimer from 'apps/Pdf/components/Disclaimer/Disclaimer';
import PlanBucket from 'apps/Pdf/components/Bucket/PlanBucket';
import PortfolioTable from 'apps/Pdf/components/Tables/PortfolioTable';
import Deposits from 'apps/Pdf/components/DespositsAndReserves/Deposits';
import GeneralDeposit from 'apps/Pdf/components/DespositsAndReserves/GeneralDeposit';
import HedgingRebalancing from 'apps/Pdf/components/HedgingRebalancing/HedgingRebalancing';
import Loading from 'apps/Pdf/components/common/Loading';
import Signature from 'apps/Pdf/components/Signature/Signature';
import ESG from 'apps/Pdf/components/ESG/ESG';
import BucketPieChart from 'apps/Pdf/components/Charts/BucketPieChart';
import PlanChart from 'apps/Pdf/components/Charts/PlanChart';

import useGeneralStyles from 'apps/Pdf/styles/general';
import useColorStyles, { colors as colorDef, colorOpacity as colorOpacityDef } from 'apps/Pdf/styles/colors';
import { InvestmentPlanContent, InvestmentPlanData } from './InvestmentPlanInterface';
import Targets from 'apps/Pdf/components/Targets/Targets';
import FutureDeposits from 'apps/Pdf/components/DespositsAndReserves/FutureDeposits';
import Reserves from 'apps/Pdf/components/DespositsAndReserves/Reserves';
import { PersonType, Currency } from 'enums';
import ChartOverview from 'apps/Pdf/components/InvestmentPlan/ChartOverview';
import ExternalProduct from 'pages/Accounts/Edit/components/ExternalProducts/ExternalProduct';
import Composition from 'pages/Accounts/Edit/Plan/Composition';
import { RebalancingAction } from 'pages/Accounts/Detail/Overview/Overview';
import Message, { MessageVariant } from 'components/Message/Message';

import { PortfolioType, PortfolioCode } from 'stores/workflowStores/newWorkflowPlanStore/model/interfaces';
import { PdfCategory } from '../Interfaces';
import Balance from 'pages/Accounts/Edit/Plan/Balance';
import ExternalProductsTable from './components/ExternalProductsTable';
import { sortExtProducts } from '../../../../pages/Accounts/Edit/components/ExternalProducts/ExternalProducts';
import { ExtProdSortBy, ExtProductCategory } from '../../../../pages/Accounts/Edit/components/ExternalProducts/model';

const isEsg = (code: PortfolioCode) => {
  return code.type === PortfolioType.ESG && code.profile !== '1';
};

const InvestmentPlan = (params: PdfContentParams) => {
  const styles = useGeneralStyles();
  const { t } = useTranslation();
  const location = useLocation();
  const planType = location.pathname.slice(0, -1);
  let templateType = TPL_INVESTMENT_PLAN;

  if (planType === makePath(TPL_NEW_INVESTMENT_PLAN)) {
    templateType = TPL_NEW_INVESTMENT_PLAN;
  }
  if (planType === makePath(TPL_DETAIL_INVESTMENT_PLAN)) {
    templateType = TPL_DETAIL_INVESTMENT_PLAN;
  }

  const pdfContent = usePdfContent<InvestmentPlanContent>({
    templateType,
    category: PdfCategory.SIGNATURE,
    ...params,
  });

  const data = pdfContent?.data ? pdfContent.data.investmentPlan : ({} as InvestmentPlanData);
  const employers = pdfContent?.data ? pdfContent.data.employers : null;
  const signature = !!pdfContent?.data?.signedInfo;
  const currency = pdfContent?.data ? pdfContent.data.currency : Currency.CZK;
  const rent = !!data.rentApplied;
  const complete = !data.portfolioOnly;
  const colors = useColorStyles({ code: pdfContent?.data?.header.signedVia || '' });
  const inflation = data?.chart?.inflation || {};
  const esgMismatch =
    data.esg &&
    ((data.buckets[0].portfolio.code.profile === '1' &&
      data.buckets[1]?.portfolio.code.profile === '1' &&
      data.buckets[2]?.portfolio.code.profile === '1') ||
      (data.portfolioOnly && data.buckets[2]?.portfolio.code.profile === '1'));

  const riskChange =
    data.buckets &&
    data.buckets.some(
      (bucket) =>
        bucket.active && bucket.originalRiskProfile !== undefined && bucket.originalRiskProfile !== bucket.riskProfile,
    );

  const esgMismatchInfo = (
    <>
      <br />
      <strong>
        Upozornění: Vybrané portfolio neodpovídá Vašim požadavkům ohledně odpovědného investování dle ESG kritérií,
        které jste uvedl v investičním dotazníku!
      </strong>
    </>
  );
  const esgInfo = (
    <P className={styles.smallText}>
      * Nižší uhlíková stopa a vyšší ESG skóre jsou vypočítány jako srovnání s tradičním portfoliem Edward se stejnou
      rizikovostí. Zastoupení kontroverzních firem zahrnuje zejména firmy s příjmy ze zbrojního průmyslu nebo tabákových
      výrobků. Bližší informace o odpovědných portfoliích Edward dle ESG kritérií najdete na našich webových stránkách{' '}
      <a href="https://www.edwardinvest.cz">www.edwardinvest.cz</a>
    </P>
  );

  const portfolioDescription = (bucket: number) => {
    const key = `${data.buckets[bucket].portfolio.code.code}_DESC`;
    const genericKey = `GENERIC_MP${data.buckets[bucket].portfolio.code.profile}`;
    return t(key, t(genericKey));
  };

  const portfolioInformation = (bucket: number) => {
    const type = data.buckets[bucket].portfolio.code.type;
    return (
      <>
        <H3>{`${
          type === PortfolioType.IND
            ? data.buckets[bucket].portfolio.code.title
            : translatePortfolioCode(data.buckets[bucket].portfolio.code.code)
        } (${translatePortfolioType(data.buckets[bucket].portfolio.code.code, true)}${
          type === PortfolioType.IND ? '*' : ''
        })`}</H3>

        {type === PortfolioType.IND ? (
          <P className="smaller">
            * Portfolio je sestaveno individuálně dle výběru požadovaných investičních kategorií klientem. V rámci každé
            kategorie portfoliomanažer vybral odpovídající investiční nástroj.
          </P>
        ) : (
          <P className={`noMargin ${styles.left}`}>{portfolioDescription(bucket)}</P>
        )}
      </>
    );
  };

  const rebalancingInfo = (
    automaticRebalancing?: boolean,
    oneTimeRebalance?: boolean,
    rebalancingTimeTest?: boolean,
  ) => {
    if ((templateType === TPL_INVESTMENT_PLAN && params.change) || templateType === TPL_NEW_INVESTMENT_PLAN) {
      let rebalancing = RebalancingAction.NONE;
      if (automaticRebalancing === true) {
        if (oneTimeRebalance === true) {
          rebalancing = RebalancingAction.AUTO_ONCE;
        } else {
          rebalancing = !rebalancingTimeTest ? RebalancingAction.AUTO_STRICT : RebalancingAction.AUTO;
        }
      } else if (oneTimeRebalance === true) {
        rebalancing = RebalancingAction.ONCE;
      }
      return (
        <Message
          id={`rebalancing`}
          message={t(`accountDetail_overview_rebalancing_${rebalancing}`)}
          variant={MessageVariant.ACTION}
          permanent
        />
      );
    } else return null;
  };

  return pdfContent ? (
    <PdfContent errors={pdfContent?.errors} className={colors.root} onload afterLoad={params.afterLoad}>
      <Title>Investiční plán</Title>

      <BasicData data={pdfContent.data!.header} plan>
        {((templateType === TPL_INVESTMENT_PLAN && params.change) || templateType === TPL_NEW_INVESTMENT_PLAN) &&
          employers && (
            <div className={`${styles.twoColumns} ${styles.lines}`}>
              <Grid container>
                <Grid item xs={4}>
                  Zaměstnavatelé:
                </Grid>
                <Grid item xs={8} className={`${styles.bold} ${styles.value}`}>
                  {employers.map((e) => (
                    <>
                      {e.companyName}, IČO: {e.companyNumber}
                      <br />
                    </>
                  ))}
                </Grid>
              </Grid>
            </div>
          )}
      </BasicData>

      <H1 className="tight">Investiční plán</H1>

      <P className="tighter">
        Níže jsou uvedeny důležité údaje, na základě kterých budeme v průběhu času spravovat Váš majetek. Je klíčové,
        aby tyto údaje odpovídaly vašim skutečným záměrům. V případě, že se některý z uvedených údajů změní, je důležité
        kontaktovat vašeho poradce a společně údaje aktualizovat.
      </P>

      <ChartOverview data={data} currency={currency} templateType={templateType} change={params.change} />

      <PlanChart
        planData={data}
        rentApplied={rent}
        currency={currency}
        portfolioOnly={data.portfolioOnly}
        showRange={data.rentApplied === false && data.targetData.targets.length === 0}
        xAxisTitle={
          data.personType === PersonType.LEGAL
            ? ''
            : `Věk klienta
          ${data.chart.metadata.partnerAgePresent ? ` / partnera` : ''}
        `
        }
        personType={data.personType}
        basicColors={{
          SHORT_TERM: colorDef.test.short,
          MID_TERM: colorDef.test.mid,
          LONG_TERM: colorDef.test.long,
        }}
        colorOpacity={{
          SHORT_TERM: colorOpacityDef.test.short,
          MID_TERM: colorOpacityDef.test.mid,
          LONG_TERM: colorOpacityDef.test.long,
        }}
      />

      <PageBreak />

      <Deposits deposits={data.depositData} currency={currency} actualInflation={0} />

      {((templateType === TPL_INVESTMENT_PLAN && params.change) ||
        templateType === TPL_NEW_INVESTMENT_PLAN ||
        templateType === TPL_DETAIL_INVESTMENT_PLAN) && (
        <P>
          {data.depositData.employerAllowed === false && 'Klient nesouhlasí s vklady od zaměstnavatele'}
          {data.depositData.employerAllowed === true && 'Klient souhlasí s vklady od zaměstnavatele'}
        </P>
      )}
      {data.depositData.employerAllowed && data.depositData.employer && (
        <GeneralDeposit deposit={data.depositData.employer} currency={currency} actualInflation={0} type="employer" />
      )}

      <Reserves data={data.reserveData} currency={currency} />

      <FutureDeposits data={data.depositData.deposits} currency={currency} actualInflation={0} />

      <Targets
        data={data.targetData?.targets}
        rentData={data.rentData}
        currency={currency}
        personType={data.personType}
        actualInflation={0}
      />

      {data.externalProductData?.externalProducts?.length !== 0 && (
        <>
          <PageBreak />

          <H2>Další zdroje</H2>
          {data.externalProductData.tableLayout ? (
            <ExternalProductsTable
              data={data.externalProductData}
              currentAccount={{
                title: pdfContent.data!.header.accountTitle,
                balance: data.balance || data?.depositData.initial?.value || { value: 0, currency: Currency.UNKNOWN },
                profit: data.buckets[2].portfolio.income,
              }}
            />
          ) : (
            <>
              {data.externalProductData.externalProducts.some((p) =>
                [ExtProductCategory.FINANCIAL_PRODUCT, ExtProductCategory.EDWARD].includes(p.category),
              ) && (
                <>
                  <h3>{t('externalProducts_title_financial')}</h3>
                  <div style={{ margin: '0 -8px', display: 'flex', flexWrap: 'wrap' }}>
                    {data.externalProductData.externalProducts
                      .filter((p) =>
                        [ExtProductCategory.FINANCIAL_PRODUCT, ExtProductCategory.EDWARD].includes(p.category),
                      )
                      .sort((a, b) => sortExtProducts(a, b, ExtProdSortBy.INST, true))
                      .map((p) => (
                        <ExternalProduct data={p} key={p.id} category={p.category} pdf />
                      ))}
                  </div>
                </>
              )}

              {data.externalProductData.externalProducts.some((p) => p.category === ExtProductCategory.PROPERTY) && (
                <>
                  <h3>{t('externalProducts_title_property')}</h3>
                  <div style={{ margin: '0 -8px', display: 'flex', flexWrap: 'wrap' }}>
                    {data.externalProductData.externalProducts
                      .filter((p) => p.category === ExtProductCategory.PROPERTY)
                      .sort((a, b) => sortExtProducts(a, b, ExtProdSortBy.INST, true))
                      .map((p) => (
                        <ExternalProduct data={p} key={p.id} category={p.category} pdf />
                      ))}
                  </div>
                </>
              )}

              {data.externalProductData.externalProducts.some((p) => p.category === ExtProductCategory.LOAN) && (
                <>
                  <h3>{t('externalProducts_title_loan')}</h3>
                  <div style={{ margin: '0 -8px', display: 'flex', flexWrap: 'wrap' }}>
                    {data.externalProductData.externalProducts
                      .filter((p) => p.category === ExtProductCategory.LOAN)
                      .sort((a, b) => sortExtProducts(a, b, ExtProdSortBy.INST, true))
                      .map((p) => (
                        <ExternalProduct data={p} key={p.id} category={p.category} pdf />
                      ))}
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}

      {data.externalProductData.externalProducts?.length !== 0 && ( //EDS-4891
        <>
          <PageBreak />
          <H2>Alokace majetku</H2>
          <Composition
            extProdData={data.externalProductData.externalProducts}
            composition={data.chart.composition}
            finInst={data.chart.metadata.financialInstitutions}
            pdf
          />

          <PageBreak />
          <H2>Rozvaha klienta</H2>
          <Balance data={data.externalProductData} currency={currency} pdf />
          <PageBreak />
        </>
      )}

      {complete ? (
        <>
          <div className={styles.avoidPageBreak}>
            <H1>Rozdělení majetku do portfolií</H1>
            <div className={`${styles.threeColumns}`}>
              <PlanBucket term="longTerm" data={data.buckets[2]} currency={currency} riskChange={riskChange} />
              <PlanBucket term="midTerm" data={data.buckets[1]} currency={currency} riskChange={riskChange} />
              <PlanBucket term="shortTerm" data={data.buckets[0]} currency={currency} riskChange={riskChange} />
            </div>
          </div>

          <Spacer />

          <H2 className="longTerm">Dlouhodobé portfolio</H2>
          <P>
            Do Dlouhodobého portfolia budeme směřovat všechny peníze, které nejsou určeny k výběru v příštích
            10&nbsp;letech. Pokud tedy nemáte nastavený v příštích 10 letech žádný cíl nebo výběr renty, budeme
            do&nbsp;dlouhodobého portfolia směřovat všechny peníze, které investujete. Peníze budeme investovat podle
            vybrané investiční strategie.
            {esgMismatch && esgMismatchInfo}
          </P>

          <H2 className="midTerm">Střednědobé portfolio</H2>
          <P>
            Do Střednědobého portfolia budeme směřovat peníze, které plánujete vybrat za 4 až 10 let. Pokud tedy máte
            nastaveny cíle, začneme odpovídající částku do portfolia přesouvat v okamžiku, kdy se přiblíží na 10 let
            od&nbsp;své splatnosti a budeme ji v portfoliu držet, dokud se splatnost cíle nezkrátí na 3 roky. Pak částku
            na cíl přesuneme do krátkodobého portfolia. Pokud budete čerpat rentu, budeme ve střednědobém portfoliu
            držet vždy částku na 7 let vyplácení renty. V portfoliu držíme navíc vámi požadovanou rezervu, pokud jste si
            ji nastavili.
            {esgMismatch && esgMismatchInfo}
          </P>

          <H2 className="shortTerm">Krátkodobé portfolio</H2>
          <P>
            Do Krátkodobého portfolia budeme směřovat peníze, které plánujete vybrat v příštích 3 letech. Pokud tedy
            máte nastaveny cíle, začneme odpovídající částku do portfolia přesouvat v okamžiku, kdy se přiblíží
            na&nbsp;3&nbsp;roky od plánovaného výběru a budeme ji v portfoliu držet, dokud ji nevyberete na svůj účet.
            Pokud budete čerpat rentu, budeme v krátkodobém portfoliu držet vždy částku na 3 roky vyplácení renty.
            V&nbsp;portfoliu držíme navíc vámi požadovanou rezervu, pokud jste si ji nastavili.
            {esgMismatch && esgMismatchInfo}
          </P>

          <PageBreak />

          <H1 className="longTerm">Dlouhodobé portfolio - detail</H1>
          <div className={styles.twoColumns}>
            <div>
              {portfolioInformation(2)}
              {isEsg(data.buckets[2].portfolio.code) && <ESG portfolio={data.buckets[2].portfolio} />}
            </div>
            <Grid item container className={styles.center}>
              <BucketPieChart data={data.buckets[2].portfolio.chart.dataset} />
              <HedgingRebalancing
                rebalancing={data.buckets[2].automaticRebalancing!}
                currency={currency}
                hedging={templateType === TPL_DETAIL_INVESTMENT_PLAN ? data.buckets[2].currencyHedging : undefined}
                rebalancingTimeTest={data.buckets[2].rebalancingTimeTest}
              />
            </Grid>
          </div>

          {rebalancingInfo(
            data.buckets[2].automaticRebalancing,
            data.buckets[2].oneTimeRebalance,
            data.buckets[2].rebalancingTimeTest,
          )}

          <PortfolioTable
            data={data.buckets[2].portfolio.assetCategories}
            terAverage={data.buckets[2].portfolio.terAverage}
            distributionTotal={data.buckets[2].portfolio.distributionTotal}
            savingIsins={data.options.specialAssets.savings}
          />
          {isEsg(data.buckets[2].portfolio.code) && esgInfo}

          <PageBreak />

          <H1 className="midTerm">Střednědobé portfolio - detail</H1>
          <div className={styles.twoColumns}>
            <div>
              {portfolioInformation(1)}
              {isEsg(data.buckets[1].portfolio.code) && <ESG portfolio={data.buckets[1].portfolio} />}
            </div>
            <Grid item container className={styles.center}>
              <BucketPieChart data={data.buckets[1].portfolio.chart.dataset} />
              <HedgingRebalancing
                rebalancing={data.buckets[1].automaticRebalancing!}
                currency={currency}
                hedging={templateType === TPL_DETAIL_INVESTMENT_PLAN ? data.buckets[1].currencyHedging : undefined}
                rebalancingTimeTest={data.buckets[1].rebalancingTimeTest}
              />
            </Grid>
          </div>

          {rebalancingInfo(
            data.buckets[1].automaticRebalancing,
            data.buckets[1].oneTimeRebalance,
            data.buckets[1].rebalancingTimeTest,
          )}

          <PortfolioTable
            data={data.buckets[1].portfolio.assetCategories}
            terAverage={data.buckets[1].portfolio.terAverage}
            distributionTotal={data.buckets[1].portfolio.distributionTotal}
            savingIsins={data.options.specialAssets.savings}
          />
          {isEsg(data.buckets[1].portfolio.code) && esgInfo}

          <PageBreak />

          <H1 className="shortTerm">Krátkodobé portfolio - detail</H1>
          <div className={styles.twoColumns}>
            <div>
              {portfolioInformation(0)}
              {isEsg(data.buckets[0].portfolio.code) && <ESG portfolio={data.buckets[0].portfolio} />}
            </div>
            <Grid item container className={styles.center}>
              <BucketPieChart data={data.buckets[0].portfolio.chart.dataset} />
              <HedgingRebalancing
                rebalancing={data.buckets[0].automaticRebalancing!}
                currency={currency}
                hedging={templateType === TPL_DETAIL_INVESTMENT_PLAN ? data.buckets[0].currencyHedging : undefined}
                rebalancingTimeTest={data.buckets[0].rebalancingTimeTest}
              />
            </Grid>
          </div>

          {rebalancingInfo(
            data.buckets[0].automaticRebalancing,
            data.buckets[0].oneTimeRebalance,
            data.buckets[0].rebalancingTimeTest,
          )}

          <PortfolioTable
            data={data.buckets[0].portfolio.assetCategories}
            terAverage={data.buckets[0].portfolio.terAverage}
            distributionTotal={data.buckets[0].portfolio.distributionTotal}
            savingIsins={data.options.specialAssets.savings}
          />
          {isEsg(data.buckets[0].portfolio.code) && esgInfo}
        </>
      ) : (
        <>
          <div className={styles.avoidPageBreak}>
            <H1>Nastavení portfolií</H1>

            <div className={`${styles.threeColumns}`}>
              <PlanBucket term="longTerm" data={data.buckets[2]} currency={currency} />
            </div>
          </div>

          <H1 className="longTerm">Investiční portfolio - detail</H1>

          <div className={styles.twoColumns}>
            <div>
              {portfolioInformation(2)}
              {isEsg(data.buckets[2].portfolio.code) && <ESG portfolio={data.buckets[2].portfolio} />}
            </div>
            <Grid item container className={styles.center}>
              <BucketPieChart data={data.buckets[2].portfolio.chart.dataset} />
              <HedgingRebalancing
                rebalancing={data.buckets[2].automaticRebalancing!}
                currency={currency}
                hedging={templateType === TPL_DETAIL_INVESTMENT_PLAN ? data.buckets[2].currencyHedging : undefined}
                rebalancingTimeTest={data.buckets[2].rebalancingTimeTest}
              />
            </Grid>
          </div>

          {rebalancingInfo(
            data.buckets[2].automaticRebalancing,
            data.buckets[2].oneTimeRebalance,
            data.buckets[2].rebalancingTimeTest,
          )}

          <PortfolioTable
            data={data.buckets[2].portfolio.assetCategories}
            terAverage={data.buckets[2].portfolio.terAverage}
            distributionTotal={data.buckets[2].portfolio.distributionTotal}
            savingIsins={data.options.specialAssets.savings}
          />
          {isEsg(data.buckets[2].portfolio.code) && esgInfo}
          {esgMismatch && esgMismatchInfo}
        </>
      )}

      <P>
        Konkrétní investiční nástroje, které budou reprezentovat vybrané investiční kategorie, jsou klientovi
        představeny společností WOOD RI v klientském portále před podpisem smluvní dokumentace. Seznam investičních
        nástrojů, které WOOD RI zařazuje do jednotlivých investičních kategorií, je veřejně k dispozici na internetových
        stránkách <a href="https://www.edwardinvest.cz">www.edwardinvest.cz</a>.
      </P>

      <Glossary
        keys={[
          'plannedDeposits',
          'requiredReserves',
          'expectedValueOfProperty',
          'requiredRent',
          'manualRent',
          'calculatedRent',
          'portfolio',
          'strategy',
          'strategyRiskiness',
          'expectedReturn',
          'acceptedLoss',
          'currencyHedging',
          'automaticRebalancing',
          'esg',
          'portfolioManager',
          'individualPortfolio',
        ]}
        useInflation={inflation.enabled}
      />

      <Disclaimer />

      <Signature protocol={signature} />
    </PdfContent>
  ) : params.preview ? (
    <Loading />
  ) : null;
};

const TPL_INVESTMENT_PLAN: TemplateType = 'investmentPlan';
const TPL_NEW_INVESTMENT_PLAN: TemplateType = 'newInvestmentPlan';
const TPL_DETAIL_INVESTMENT_PLAN: TemplateType = 'detailInvestmentPlan';

export { TPL_INVESTMENT_PLAN, TPL_NEW_INVESTMENT_PLAN, TPL_DETAIL_INVESTMENT_PLAN, InvestmentPlan };
